import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action } from 'redux';

import { AddressTypeEnum, ServerErrorCode } from '../../../lib/enums';
import lang from '../../../lib/language';
import {
  CountryData,
  ENROLL_REQUEST_STATE,
  IndividualExtendedInfo,
  IndividualExtendedInfoData,
} from '../../../lib/models/enroll';
import { ENROLL_AUTH_REQUEST_TYPE } from '../../../lib/models/enroll-requests';
import { RootState } from '../../../lib/store';
import { enrollAuth } from '../../../lib/store/auth';
import { logout } from '../../../lib/store/common-actions';
import { hasValidCookie } from '../../../lib/store/selectors';
import CountriesCache from '../../../lib/store-util/CountriesCache';
import { checkErrorSatusCode } from '../../../lib/util/error-handling/ErrorHandlingHelpers';
import { NullableNumber, NullableString } from '../../../lib/util/types';
import {
  createFormControlError,
  getPhoneNumberValidity,
  validateControl,
} from '../../../lib/util/ValidationSchemes/Validate';
import { ControlError } from '../../../lib/util/ValidationSchemes/ValidationTypes';

import InputErrorIcon from '../../../assets/img/input-error-icon.svg';
import { PhoneAutocomplete } from '../../../components';
import { ICountryControlOptions } from '../../../components/CountryControl/CountryControl.types';
import {
  PhoneAutocompleteValue,
} from '../../../components/PhoneAutocomplete/PhoneAutocomplete.types';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../../Enrollment/component/Layout';
import { MobileCountryCodeSelector, PhoneNumberSelector } from '../Verification.constants';

import styles from './VerificationPhoneNumber.module.scss';

type OwnProps = {};
type Props = OwnProps & ReduxProps;
type LocationState = {
  [MobileCountryCodeSelector]?: ICountryControlOptions;
};

const getKnownServerErrorMessage = (errorCode: NullableString): NullableString => {
  if (errorCode === ServerErrorCode.DuplicatePhoneNumber) {
    return lang.commonOnboardingPhoneVerificationDuplicatePhoneNumber();
  }

  return null;
};

const VerificationPhoneNumber = (props: Props) => {
  const { sendPhoneCode, appLogOut, isAppLoaded, isAuthenticated } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const countries = CountriesCache.use();

  const requestState = useSelector<RootState, ENROLL_REQUEST_STATE | undefined>(
    (state: RootState) => state.auth.enroll.status,
  );
  const statusCode = useSelector<RootState, number | undefined>(
    (state: RootState) => state.auth.enroll.statusCode,
  );
  const errorCode = useSelector<RootState, NullableString>(
    (state: RootState) => state.auth.enroll.errorCode,
  );
  const errorDescription = useSelector<RootState, NullableString>(
    (state: RootState) => state.auth.enroll.errorDescription,
  );

  const phoneCodeBySelectedCountry = useSelector<RootState, NullableString>(
    (state: RootState) =>
      countries?.find((item) => item.id === state.auth.enroll.country)?.phone_code,
  );

  const phoneCode = useSelector<RootState, string | null | undefined>(
    (state: RootState) => state.auth.enroll.phoneCode,
  );
  const phoneNumber = useSelector<RootState, string | null | undefined>(
    (state: RootState) => state.auth.enroll.phoneNumber,
  );
  const individual = useSelector<RootState, IndividualExtendedInfoData | null>(
    (state: RootState) => state.crm.individualExtendedInfo,
  );
  const userId = useSelector<RootState, NullableNumber>(
    (state: RootState) =>
      state.auth.enroll.userId ?? state.auth.enrollUserId ?? state.crm.individualExtendedInfo?.user_correlation_id,
  );

  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [isClickedBtnContinue, setIsClickedBtnContinue] = useState<boolean>(false);
  const [value, setValue] = useState<PhoneAutocompleteValue>({
    phone: '',
    code: '',
    countryId: '',
  });
  const [hasMadeRequest, setHasMadeRequest] = useState<boolean>(false);
  const [sendCodeError, setSendCodeError] = useState<ControlError>();
  const { form } = EnrollmentState.use();

  const options = useMemo((): ICountryControlOptions[] => {
    if (countries && countries.length > 0) {
      return countries
        .filter((country: CountryData) => country.id && country.name && country.phone_code)
        .sort((current: CountryData, next: CountryData) => {
          if (current.name.toLowerCase() < next.name.toLowerCase()) {
            return -1;
          }
          if (current.name.toLowerCase() > next.name.toLowerCase()) {
            return 1;
          }
          return 0;
        })
        .map((country: CountryData) => ({
          label: country.name,
          value: `${country.phone_code} ${country.name}`,
          countryId: country.id,
        }));
    }

    return [];
  }, [countries]);

  const onNextClick = useCallback(() => {
    const countryCode = value.code.split(' ')[0];
    setSendCodeError(undefined);
    sendPhoneCode(countryCode, value.phone, userId);
    setHasMadeRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendPhoneCode, userId, value]);

  const validateForm = useCallback((): boolean => {
    const codeCheck = validateControl(
      value.code,
      [],
      lang.commonOnboardingPhoneVerificationCountry(),
      true,
    );

    const phoneCheck = getPhoneNumberValidity(value.phone);

    return codeCheck.isValid && phoneCheck.isValid;
  }, [value]);

  useEffect(() => {
    if (!isAppLoaded) {
      return;
    }

    if (!form.steps.isVerifyEmailPassed && !isAuthenticated) {
      navigate(UI_ROUTES.emailConfirm, { replace: true });
    } else {
      EnrollmentState.setVerifyEmailStepPassed(true);
    }
  }, [form.steps.isVerifyEmailPassed, isAppLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCountryCodeValueByPhoneCode = (phoneCountyCode: string): string =>
    options.find((el) => el.value.toString().includes(phoneCountyCode))!.value.toString();

  useEffect(() => {
    if (!isInitialised) {
      const selectedCountryFromMobileSearch = locationState?.[MobileCountryCodeSelector];

      if (selectedCountryFromMobileSearch) {
        setValue((currState: PhoneAutocompleteValue) => ({
          ...currState,
          code: selectedCountryFromMobileSearch.value.toString(),
          countryId: selectedCountryFromMobileSearch.countryId,
        }));
        setIsInitialised(true);
        return;
      }

      if (phoneCode && phoneNumber) {
        const countryCodeValue = getCountryCodeValueByPhoneCode(phoneCode);

        setValue((currState: PhoneAutocompleteValue) => ({
          ...currState,
          phone: phoneNumber,
          code: countryCodeValue,
        }));
        setIsInitialised(true);
        return;
      }
      if (individual && individual.addresses && individual.addresses.length > 0) {
        const individualModel = new IndividualExtendedInfo(individual);
        const residentialAddress = individualModel.getAddressByType(AddressTypeEnum.Residential);

        const filteredOptions = options?.find(
          (option: ICountryControlOptions) => option.countryId === residentialAddress.country_id,
        );

        if (filteredOptions) {
          setValue((currState: PhoneAutocompleteValue) => ({
            ...currState,
            code: filteredOptions.value.toString(),
            countryId: filteredOptions.countryId,
          }));
          setIsInitialised(true);
          return;
        }
      }

      if (phoneCodeBySelectedCountry) {
        setValue((currState: PhoneAutocompleteValue) => ({
          ...currState,
          code: getCountryCodeValueByPhoneCode(phoneCodeBySelectedCountry!),
        }));
        setIsInitialised(true);
      }
    }
  }, [individual, isInitialised, options, phoneCode, phoneCodeBySelectedCountry, phoneNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasMadeRequest) {
      if (requestState === ENROLL_REQUEST_STATE.READY) {
        navigate(UI_ROUTES.enrollPhoneVerificationOtp, {
          state: {
            [PhoneNumberSelector]: value,
          },
        });
      } else if (requestState === ENROLL_REQUEST_STATE.ERROR) {
        // Network/timeout failures carry no usable http status, so fall back to the generic message
        const statusMessage = checkErrorSatusCode(
          statusCode ?? 0,
          lang.commonOnboardingPhoneVerificationCodeClientErrorMessage(),
          lang.commonOnboardingPhoneVerificationCodeSendAgainErrorMessage(),
        );
        const errorMsg = createFormControlError(
          getKnownServerErrorMessage(errorCode)
          || errorDescription
          || statusMessage
          || lang.commonOnboardingPhoneVerificationCodeSendAgainErrorMessage(),
        );
        setSendCodeError(errorMsg);
        setHasMadeRequest(false);
      }
    }
  }, [statusCode, errorCode, errorDescription, hasMadeRequest, navigate, requestState, value]);

  const handleSubmit = (): void => {
    setIsClickedBtnContinue(true);

    if (validateForm()) {
      onNextClick();
    }
  };

  return (
    <Layout
      formTitle=""
      contentStyle={styles.verificationContent}
      displayBackButton={false}
      displayFooter={false}
      displayNextButton
      nextButtonText={lang.commonContinueButton()}
      displayLoginButton={false}
      onSubmit={handleSubmit}
      onBackBtnPress={appLogOut}
    >
      <div className={styles.text}>
        <div className={styles.title}>{lang.commonVerificationPhoneNeeded()}</div>
        <div className={styles.subtitle}>{lang.commonVerificationPhoneNeededSubtitle()}</div>
      </div>
      <PhoneAutocomplete
        value={value}
        options={options}
        onChange={(controlValue: PhoneAutocompleteValue) => {
          setSendCodeError(undefined);
          setValue(controlValue);
        }}
        isClickedBtnContinue={isClickedBtnContinue}
      />
      {sendCodeError && (
        <div id={sendCodeError.id.toString()} className={styles.error}>
          <img src={InputErrorIcon} className={styles.errorIcon} alt="error" />
          {sendCodeError.error}
        </div>
      )}
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  appLogOut: () => dispatch(logout()),
  sendPhoneCode: (phoneCode: string, phoneNumber: string, userId: NullableNumber) =>
    dispatch(
      enrollAuth({
        requestType: ENROLL_AUTH_REQUEST_TYPE.SEND_PHONE_CODE,
        phoneCode,
        phoneNumber,
        userId,
      }),
    ),
});

const mapStateToProps = (state: RootState) => ({
  isAppLoaded: state.app.loaded,
  isAuthenticated: hasValidCookie(state) && state.app.loaded,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(VerificationPhoneNumber);
